import { useEffect, useMemo, useState } from 'react'
import { authApi, coursesApi, onboardingApi, schoolsApi } from '../../services/api'
import OnboardingSteps from './OnboardingSteps'

const LEVELS = ['100', '200', '300', '400', '500', 'Masters', 'PhD']
const NIGERIAN_STATES = [
  'Abia',
  'Adamawa',
  'Akwa Ibom',
  'Anambra',
  'Bauchi',
  'Bayelsa',
  'Benue',
  'Borno',
  'Cross River',
  'Delta',
  'Ebonyi',
  'Edo',
  'Ekiti',
  'Enugu',
  'FCT',
  'Gombe',
  'Imo',
  'Jigawa',
  'Kaduna',
  'Kano',
  'Katsina',
  'Kebbi',
  'Kogi',
  'Kwara',
  'Lagos',
  'Nasarawa',
  'Niger',
  'Ogun',
  'Ondo',
  'Osun',
  'Oyo',
  'Plateau',
  'Rivers',
  'Sokoto',
  'Taraba',
  'Yobe',
  'Zamfara'
]

const buildProfilePayload = (form, { includeAll = false } = {}) => {
  const payload = {}
  const keys = ['school', 'department', 'level', 'state', 'matric']

  for (const k of keys) {
    const v = typeof form[k] === 'string' ? form[k].trim() : form[k]
    if (includeAll) {
      payload[k] = v
      continue
    }
    if (typeof v === 'string' && v.length > 0) payload[k] = v
  }
  return payload
}

const validateStep = (step, form) => {
  const errors = {}
  const school = (form.school || '').trim()
  const department = (form.department || '').trim()
  const level = (form.level || '').trim()
  const matric = (form.matric || '').trim()
  const state = (form.state || '').trim()

  if (step === 0) {
    if (!school) errors.school = 'School is required.'
    if (!department) errors.department = 'Department is required.'
  } else if (step === 1) {
    if (!level) errors.level = 'Level is required.'
    if (!matric) errors.matric = 'Matriculation number is required.'
  } else if (step === 2) {
    if (!state) errors.state = 'State is required.'
  }

  return errors
}

const validateAll = (form) => {
  return {
    ...(validateStep(0, form)),
    ...(validateStep(1, form)),
    ...(validateStep(2, form))
  }
}

const OnboardingModal = ({ open, initialProfile, onClose, onRefetch }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState(initialProfile || null)

  const [schools, setSchools] = useState([])
  const [courses, setCourses] = useState([])

  const [step, setStep] = useState(0)
  const [fieldErrors, setFieldErrors] = useState({})
  const [apiError, setApiError] = useState('')

  const initialForm = useMemo(() => {
    const p = profile || {}
    return {
      school: p.school || '',
      department: p.department || '',
      level: p.level || '',
      matric: p.matric || '',
      state: p.state || ''
    }
  }, [profile])

  const [form, setForm] = useState(initialForm)

  const matchedSchool = useMemo(() => {
    const schoolName = (form.school || '').trim()
    if (!schoolName) return null
    return schools.find((s) => (s.name || '').trim() === schoolName) || null
  }, [schools, form.school])

  const [schoolId, setSchoolId] = useState('')
  const isCustomSchool = !matchedSchool && (form.school || '').trim().length > 0

  useEffect(() => {
    // When parent passes new profile or modal opens, initialize state.
    if (!open) return
    setStep(0)
    setFieldErrors({})
    setApiError('')
    setSaving(false)
    setLoading(false)
    setProfile(initialProfile || null)
  }, [open, initialProfile])

  useEffect(() => {
    if (!open) return
    setForm(initialForm)
  }, [open, initialForm])

  useEffect(() => {
    if (!open) return
    const load = async () => {
      try {
        setLoading(true)
        setApiError('')

        let me = initialProfile
        if (!me) {
          const meRes = await authApi.me()
          me = meRes?.data?.data
        }

        setProfile(me)

        const schoolsRes = await schoolsApi.list()
        setSchools(schoolsRes?.data?.data || [])
      } catch (e) {
        setApiError(e?.response?.data?.message || 'Failed to load onboarding details.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return

    const nextSchoolId = matchedSchool ? matchedSchool._id : (form.school || '').trim() ? '' : ''
    setSchoolId(nextSchoolId)
  }, [open, matchedSchool]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    if (!schoolId) {
      setCourses([])
      return
    }

    coursesApi
      .list({ schoolId })
      .then((res) => setCourses(res?.data?.data || []))
      .catch(() => setCourses([]))
  }, [open, schoolId])

  const onFieldChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }))
    if (apiError) setApiError('')
  }

  const handleSchoolSelect = (e) => {
    const id = e.target.value

    if (!id) {
      setSchoolId('')
      setForm((prev) => ({ ...prev, school: '' }))
      setCourses([])
      setFieldErrors({})
      return
    }

    if (id === '_other') {
      setSchoolId('')
      setForm((prev) => ({ ...prev, school: '', department: '' }))
      setCourses([])
      return
    }

    if (id === '_current') {
      setSchoolId('')
      // Keep typed value in form.school
      setCourses([])
      return
    }

    const school = schools.find((s) => s._id === id)
    setSchoolId(id)
    setForm((prev) => ({ ...prev, school: school?.name || '', department: '' }))
    setCourses([])
  }

  const handleDepartmentSelect = (e) => {
    setForm((prev) => ({ ...prev, department: e.target.value }))
    setFieldErrors((prev) => ({ ...prev, department: undefined }))
  }

  const handleSkip = async () => {
    if (saving || loading) return
    setSaving(true)
    setApiError('')

    try {
      // Persist whatever has been entered so far.
      const payload = buildProfilePayload(form)
      if (Object.keys(payload).length > 0) {
        await authApi.updateProfile(payload)
      }

      await onboardingApi.skip()

      onClose?.({ status: 'skipped' })
      if (onRefetch) await onRefetch()
    } catch (e) {
      setApiError(e?.response?.data?.message || 'Failed to skip onboarding.')
    } finally {
      setSaving(false)
    }
  }

  const handleComplete = async () => {
    if (saving || loading) return
    setSaving(true)
    setApiError('')

    try {
      const errors = validateAll(form)
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        setSaving(false)
        return
      }

      await authApi.updateProfile(buildProfilePayload(form, { includeAll: true }))
      await onboardingApi.complete()

      onClose?.({ status: 'completed' })
      if (onRefetch) await onRefetch()
    } catch (e) {
      const msg = e?.response?.data?.message || 'Failed to complete onboarding.'
      setApiError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleNext = () => {
    const errors = validateStep(step, form)
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return
    setStep((s) => Math.min(2, s + 1))
  }

  const handleBack = () => setStep((s) => Math.max(0, s - 1))

  if (!open) return null

  return (
    <dialog
      className="modal modal-bottom sm:modal-middle"
      open
      role="dialog"
      aria-modal="true"
      aria-label="Student onboarding"
      onCancel={(e) => {
        // ESC or cancel should behave like "Skip for now" to keep DB state consistent.
        e.preventDefault()
        void handleSkip()
      }}
    >
      <div className="modal-box w-11/12 max-w-5xl bg-base-100 text-base-content border border-base-300 shadow-2xl p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="badge badge-outline text-[#0BBF64] border-[#0BBF64] mb-2">Onboarding</div>
            <h3 className="font-extrabold text-lg sm:text-xl">Complete your student details</h3>
            <p className="text-sm text-base-content/70 mt-1">
              This takes about 1 minute. You can skip and finish later from Settings.
            </p>
          </div>
          <div className="hidden sm:block">
            <div className="text-right">
              <div className="text-xs text-base-content/60">Step {step + 1} of 3</div>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <OnboardingSteps stepIndex={step} />
        </div>

        {apiError && <p className="mt-4 text-error text-sm">{apiError}</p>}

        <div className="mt-4">
          {loading ? (
            <div className="py-10 flex justify-center">
              <span className="loading loading-spinner loading-lg" />
            </div>
          ) : (
            <div className="transition-all duration-300">
              {step === 0 && (
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <label className="form-control">
                      <span className="label-text font-medium">School</span>
                      <select
                        name="school"
                        className="select select-bordered w-full bg-white text-slate-900"
                        value={schoolId || (form.school.trim() ? '_current' : '')}
                        onChange={handleSchoolSelect}
                      >
                        <option value="">Select your school</option>
                        <option value="_other">Other (type manually)</option>
                        {schools.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.name}
                          </option>
                        ))}
                        {form.school.trim() &&
                          !matchedSchool &&
                          !schools.some((s) => (s.name || '').trim() === form.school.trim()) && (
                            <option value="_current">{form.school}</option>
                          )}
                      </select>
                      {isCustomSchool && (
                        <input
                          type="text"
                          name="school"
                          className="input input-bordered w-full mt-2 bg-white text-slate-900"
                          value={form.school}
                          onChange={onFieldChange}
                          placeholder="Type your school name"
                        />
                      )}
                      {fieldErrors.school && <p className="text-error text-xs mt-1">{fieldErrors.school}</p>}
                    </label>

                    <label className="form-control">
                      <span className="label-text font-medium">Department / Course</span>
                      {isCustomSchool ? (
                        <input
                          type="text"
                          name="department"
                          className="input input-bordered w-full bg-white text-slate-900"
                          value={form.department}
                          onChange={onFieldChange}
                          placeholder="e.g. Computer Science"
                        />
                      ) : (
                        <select
                          name="department"
                          className="select select-bordered w-full bg-white text-slate-900"
                          value={form.department}
                          onChange={handleDepartmentSelect}
                        >
                          <option value="">Select department</option>
                          {courses.map((c) => (
                            <option key={c._id} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                          {form.department.trim() && !courses.some((c) => c.name === form.department) && (
                            <option value={form.department}>{form.department}</option>
                          )}
                        </select>
                      )}
                      {fieldErrors.department && (
                        <p className="text-error text-xs mt-1">{fieldErrors.department}</p>
                      )}
                    </label>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <label className="form-control">
                    <span className="label-text font-medium">Level</span>
                    <select
                      name="level"
                      className="select select-bordered w-full bg-white text-slate-900"
                      value={form.level}
                      onChange={onFieldChange}
                    >
                      <option value="">Select level</option>
                      {LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.level && <p className="text-error text-xs mt-1">{fieldErrors.level}</p>}
                  </label>

                  <label className="form-control">
                    <span className="label-text font-medium">Matric Number</span>
                    <input
                      type="text"
                      name="matric"
                      className="input input-bordered w-full bg-white text-slate-900"
                      value={form.matric}
                      onChange={onFieldChange}
                      placeholder="e.g. 2020/CS/1234"
                    />
                    {fieldErrors.matric && <p className="text-error text-xs mt-1">{fieldErrors.matric}</p>}
                  </label>
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-1 gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <label className="form-control">
                      <span className="label-text font-medium">State</span>
                      <select
                        name="state"
                        className="select select-bordered w-full bg-white text-slate-900"
                        value={form.state}
                        onChange={onFieldChange}
                      >
                        <option value="">Select your state</option>
                        {NIGERIAN_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.state && <p className="text-error text-xs mt-1">{fieldErrors.state}</p>}
                    </label>
                  </div>

                  <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                    <div className="font-semibold text-sm mb-2">Review</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
                      <div>
                        <span className="text-slate-500">School:</span> {form.school || '—'}
                      </div>
                      <div>
                        <span className="text-slate-500">Department:</span> {form.department || '—'}
                      </div>
                      <div>
                        <span className="text-slate-500">Level:</span> {form.level || '—'}
                      </div>
                      <div>
                        <span className="text-slate-500">Matric:</span> {form.matric || '—'}
                      </div>
                      <div>
                        <span className="text-slate-500">State:</span> {form.state || '—'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-action mt-5 flex flex-col sm:flex-row gap-3 sm:gap-2 w-full">
          <button
            type="button"
            className="btn btn-ghost w-full sm:w-auto"
            onClick={() => void handleSkip()}
            disabled={saving || loading}
          >
            Skip for now
          </button>

          <div className="flex-1" />

          <button
            type="button"
            className={`btn ${step === 0 ? 'btn-disabled' : 'btn-outline'} w-full sm:w-auto`}
            onClick={handleBack}
            disabled={saving || loading || step === 0}
          >
            Back
          </button>

          {step < 2 ? (
            <button
              type="button"
              className="btn bg-[#0BBF64] hover:bg-[#0aa459] text-white w-full sm:w-auto"
              onClick={handleNext}
              disabled={saving || loading}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="btn bg-[#0BBF64] hover:bg-[#0aa459] text-white w-full sm:w-auto"
              onClick={() => void handleComplete()}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Complete onboarding'}
            </button>
          )}
        </div>
      </div>
      <form method="dialog" className="modal-backdrop bg-black/35">
        <button type="button" aria-label="Close modal" onClick={() => void handleSkip()}>
          close
        </button>
      </form>
    </dialog>
  )
}

export default OnboardingModal

